import { useCallback, useContext } from 'react';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ActivityIndicator } from '@trinity/chat-components/components/presentation/ActivityIndicator.js';
import { Button } from '@trinity/chat-components/components/presentation/Button/index.js';
import { WorkspaceSelect } from '@/components/Workspace/WorkspaceSelect';
import { WorkspaceFolderTree } from '@/components/Workspace.Folder/Folder.Tree';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context.js';
import { MoveObjectWorkspaceContext } from '@/containers/WorkspaceObject/Context';
import { useMoveObjectContext } from '@/containers/WorkspaceObject/hooks/useMoveObjectContext';
import { useModal, type UseModalProps } from '@trinity/chat-components/components/Modal/index.js';
import styles from './style/MoveObjectModal.module.css';

type Props = {} & UseModalProps;

export const MoveObjectModal = ({ onClose, open }: Props) => {
  const {
    loadingWorkspaces,
    onSelectWorkspace,
    selectedWorkspace,
    workspaces,
  } = useContext(MoveObjectWorkspaceContext);
  const {
    currentPath,
    data,
    object,
    onSave,
    isSaving,
    loadingTree,
    parentObjectId,
    onSelectParentObject,
  } = useMoveObjectContext();

  const handleSave = useCallback(() => {
    return onSave()
      .then(onClose);
  }, [
    onClose,
    onSave,
  ]);

  const ctx = useContext(AppThemingContext);

  const Footer = useCallback(() => {
    const canSave = !!parentObjectId &&
      parentObjectId !== object?.parentObjectId;

    const style = {
      backgroundColor: ctx?.enabled && canSave
        ? ctx?.palette?.primary?.main
        : undefined,
      borderColor: ctx?.enabled && canSave
        ? ctx?.palette?.primary?.main
        : undefined,
      color: ctx?.enabled && canSave
        ? ctx?.palette?.primary?.text
        : undefined,
    };

    return (
      <ButtonSet>
        <Button.Destructive
          className={styles.btn}
          onClick={onClose}
          variant="brick">
          Cancel
        </Button.Destructive>
        <Button.Primary
          className={styles.btn}
          disabled={!canSave || isSaving}
          onClick={handleSave}
          style={style}
          variant="brick">
          Move
        </Button.Primary>
      </ButtonSet>
    );
  }, [
    handleSave,
    object?.parentObjectId,
    onClose,
    parentObjectId,
    ctx?.enabled,
    ctx?.palette?.primary?.main,
    ctx?.palette?.primary?.text,
    isSaving,
  ]);

  const title = `Move ${object?.name}`;

  if (loadingTree || loadingWorkspaces) {
    return null;
  }

  return (
    <Modal
      className={styles.root}
      onClose={onClose}
      open={open}>
      <Header
        className={styles.header}
        text={title} />
      <div className={styles.item}>
        <div className={styles.label}>Current Location:</div>
        <div className={styles.path}>{currentPath[currentPath.length - 1]}</div>
      </div>
      <div className={styles.item}>
        <div className={styles.label}>Destination:</div>
        <WorkspaceSelect
          className={styles.select}
          value={selectedWorkspace}
          onChange={onSelectWorkspace}
          options={workspaces} />
      </div>
      <div className={styles.body}>
        {loadingTree || loadingWorkspaces && <ActivityIndicator />}
        {!loadingTree && !loadingWorkspaces &&
          <WorkspaceFolderTree
            data={data}
            onNodeSelection={onSelectParentObject} />}
      </div>
      <Footer />
    </Modal>
  );
};

export const useMoveObjectModal = () => useModal(MoveObjectModal);

export default MoveObjectModal;